import {
  createContext,
  type ComponentPropsWithoutRef,
  type MouseEvent,
  type ReactNode,
  useContext,
} from "react";

export type PlaygroundRouteNamespace = "playground" | "chat-standalone";

export type PlaygroundNavigateOptions = {
  replace?: boolean;
};

export type PlaygroundRouting = {
  id?: string;
  pathname: string;
  routeNamespace: PlaygroundRouteNamespace;
  navigate: (
    destination: string,
    options?: PlaygroundNavigateOptions,
  ) => void;
  newChatDestination: string;
  buildConversationDestination: (conversationId: string) => string;
  buildStandaloneDestination: (conversationId?: string) => string;
  navigateToNewChat: (options?: PlaygroundNavigateOptions) => void;
  navigateToConversation: (
    conversationId: string,
    options?: PlaygroundNavigateOptions,
  ) => void;
};

const PlaygroundRoutingContext = createContext<PlaygroundRouting | null>(null);

export const getPlaygroundRouteNamespace = (
  pathname: string,
): PlaygroundRouteNamespace =>
  pathname.split("/")[1] === "chat-standalone"
    ? "chat-standalone"
    : "playground";

export const buildPlaygroundNewChatDestination = (
  routeNamespace: PlaygroundRouteNamespace,
) => `/${routeNamespace}`;

export const buildPlaygroundConversationDestination = (
  routeNamespace: PlaygroundRouteNamespace,
  conversationId: string,
) => `/${routeNamespace}/${conversationId}`;

export const buildPlaygroundStandaloneDestination = (
  conversationId?: string,
) => `/chat-standalone/${conversationId ?? ""}`;

export const PlaygroundRoutingProvider = ({
  children,
  value,
}: {
  children: ReactNode;
  value: PlaygroundRouting;
}) => {
  return (
    <PlaygroundRoutingContext.Provider value={value}>
      {children}
    </PlaygroundRoutingContext.Provider>
  );
};

export const usePlaygroundRouting = () => {
  const routing = useContext(PlaygroundRoutingContext);

  if (!routing) {
    throw new Error(
      "usePlaygroundRouting must be used within PlaygroundRoutingProvider",
    );
  }

  return routing;
};

type PlaygroundLinkProps = Omit<
  ComponentPropsWithoutRef<"a">,
  "href"
> & {
  to: string;
  replace?: boolean;
};

export const PlaygroundLink = ({
  to,
  replace,
  onClick,
  target,
  ...props
}: PlaygroundLinkProps) => {
  const { navigate } = usePlaygroundRouting();

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(event);

    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      (target && target !== "_self")
    ) {
      return;
    }

    event.preventDefault();
    navigate(to, { replace });
  };

  return (
    <a {...props} href={to} target={target} onClick={handleClick} />
  );
};
